import store from '../../store';
import { setBatchIndex } from '../../actions/chatManager';
import { isEmpty } from 'Helpers/objects';
import { ITEM, USER } from 'Constants/chat';

let QUEUE_LENGTH = 5;
const CHAT_LOAD_DELAY = 100;

export function setQueueLength(length) {
    QUEUE_LENGTH = length || QUEUE_LENGTH;
}

function getUserData(userId) {
    const state = store.getState() || {};
    const users = state && state.users;

    return users.elements ? users.elements[userId] : null;
}

function getItemData(itemId) {
    const state = store.getState() || {};
    const items = state && state.items;

    return items.elements ? items.elements[itemId] : null;
}

function getCurrentBatch() {
    const state = store.getState() || {};
    const batchIndex = state && state.chat && state.chat.batchIndex;

    return batchIndex;
}

function getThreadData(userId, itemId) {
    const state = store.getState() || {};
    const threads = state && state.chat && state.chat.threadMessages;

    return threads && threads[`${userId}_${itemId}`];
}

function getThreadMeta(userId, itemId) {
    const state = store.getState() || {};
    const threads = state && state.chat && state.chat.threads;

    return threads && threads.find(thread => thread.id === `${userId}_${itemId}`);
}

export default class ChatDataLoader {
    static requestQueue = [];
    static requestServed = 0;
    static uniqueItemIds = {};
    static uniqueUserIds = {};
    static counter = 0;
    static messageIds = [];
    static batchedLength = 0;

    static loadThreadDataWithBatch(batchIndex, userId, itemId, fetchers, resetRequestQueue, batchedLength) {
        if (batchIndex > getCurrentBatch()) {
            return;
        }
        this.loadThreadData(userId, itemId, fetchers, resetRequestQueue, batchedLength);
    }

    static loadThreadData(userId, itemId, fetchers, resetRequestQueue = () => {}, batchedLength = 0) {
        this.batchedLength = batchedLength;
        if (this.batchedLength > 0) {
            this.counter += 1;
        }
        this.checkUserData(userId, fetchers.userFetch);
        this.checkItemData(itemId, fetchers.itemFetch, fetchers.isBulkType);
        this.checkMessageData(userId, itemId, fetchers.messageFetch, resetRequestQueue);
    }

    static handleBulkAPICall(data) {
        const { uniqueIds, id, type, fetcher, info } = data || {};
        const isUniqueVal = !info && !uniqueIds[id];
        const isCounterCompleted = this.counter === QUEUE_LENGTH && this.batchedLength !== 0;
        const isBatchCompleted = this.counter > 0 && this.counter === this.batchedLength;

        if (isUniqueVal) {
            uniqueIds[id] = true;
        }

        if ((isCounterCompleted || isBatchCompleted)) {
            if (isEmpty(uniqueIds)) {
                this.incrementRequestServed();
            }
            else {
                const idsInString = Object.keys(uniqueIds).toString();

                this.bulkfetcher(idsInString, fetcher);
            }
        }
        else if (this.batchedLength === 0 && !info && typeof id !== 'undefined') {
            if (type == USER) {
                fetcher({ userId: id });
            }
            else {
                this.fetchItem(id, fetcher);
            }
        }
    }

    static checkUserData(userId, userFetcher) {
        const userInfo = getUserData(userId);
        const data = {
            info: userInfo,
            id: userId,
            type: USER,
            uniqueIds: this.uniqueUserIds,
            fetcher: userFetcher
        };

        this.handleBulkAPICall(data);
    }

    static bulkfetcher(ids, fetcher) {
        this.addToRequestQueue(fetcher({ ids }));
    }

    static checkItemData(itemId, itemFetcher, isBulkType = false) {
        const itemInfo = getItemData(itemId);

        if (isBulkType) {
            const data = {
                info: itemInfo,
                id: itemId,
                type: ITEM,
                uniqueIds: this.uniqueItemIds,
                fetcher: itemFetcher
            };

            this.handleBulkAPICall(data);
        }
        else if (itemInfo || this.uniqueItemIds[itemId]) {
            this.incrementRequestServed();
        }
        else {
            this.uniqueItemIds[itemId] = true;
            this.fetchItem(itemId, itemFetcher);
        }
    }

    static fetchItem(itemId, itemFetcher) {
        this.addToRequestQueue(itemFetcher({ itemId }));
    }

    static checkMessageData(userId, itemId, messageFetcher, resetRequestQueue) {
        const threadInfo = getThreadData(userId, itemId);
        const threadMeta = getThreadMeta(userId, itemId);
        let loadMessages = true;

        resetRequestQueue();

        if (threadInfo) {
            if (threadMeta) {
                if (threadMeta.bDidLoadMessages) {
                    if (threadMeta.bNeedsUpdate) {
                        loadMessages = threadInfo.reduce((acc, val) => {
                            acc[0] = (acc[0] === undefined || val.date > acc[0]) ? val.date : acc[0];
                            return acc;
                        }, []);
                    }
                    else {
                        loadMessages = false;
                    }
                }
            }
        }

        if (loadMessages && this.batchedLength > 0) {
            const obj = { peer_id: userId, ad_id: itemId, time: typeof loadMessages === 'number' ? loadMessages : 0 };

            this.messageIds.push(obj);
        }

        if (this.counter === QUEUE_LENGTH || (this.counter > 0 && this.counter === this.batchedLength)) {
            if (this.messageIds.length > 0) {
                this.fetchMessages(this.messageIds, messageFetcher);
            }
            else {
                this.incrementRequestServed();
            }
        }
        else if (this.batchedLength === 0) {
            const obj = { peer_id: userId, ad_id: itemId, time: typeof loadMessages === 'number' ? loadMessages : 0 };

            messageFetcher([obj]);
        }
    }

    static fetchMessages(queryParams, messageFetcher) {
        this.addToRequestQueue(messageFetcher(queryParams));
    }

    static addToRequestQueue(req) {
        this.requestQueue.push(req);
        if (this.checkQueueLength()) {
            this.attachQueueCompleteEvent();
        }
    }

    static incrementRequestServed() {
        this.requestServed++;
        if (this.checkQueueLength()) {
            this.attachQueueCompleteEvent();
        }
    }

    static resetRequests() {
        this.requestQueue = [];
        this.requestServed = 0;
        this.uniqueItemIds = {};
        this.uniqueUserIds = {};
        this.counter = 0;
        this.messageIds = [];
    }

    static resetQueue() {
        if (this.requestServed === QUEUE_LENGTH) {
            this.resetRequests();
            setTimeout(() => {
                store.dispatch(setBatchIndex(getCurrentBatch() + 1));
            }, CHAT_LOAD_DELAY);
        }
        else {
            this.resetRequests();
            store.dispatch(setBatchIndex(getCurrentBatch() + 1));
        }
    }

    static checkQueueLength() {
        return this.requestQueue.length === QUEUE_LENGTH - 2 - this.requestServed;
    }

    static attachQueueCompleteEvent() {
        Promise.all(this.requestQueue).then(() => {
            this.resetQueue();
        }).catch(() => {
            this.resetQueue();
        });
    }
}
